import React, { FC, useContext } from 'react'
import { Button, Grid } from '@mui/material'

import { TRANSLATIONS as T } from '../../constants/translations-constants'
import { Events } from '../../FSM/shared/constants'
import { PAYMENT_METHOD_AVTALEGIRO } from '../../constants/auth-constants'
import PolicyCard from './PolicyCard'
import { Typography } from '@dg-shared'
import { useTranslate } from '../../hooks/shared'
import { PolicyV2Type } from '../../types/ApiV2'
import { SELECTED_POLICY_NUMBER } from '../../constants'
import { Context } from '@dg-shared/ContextProvider'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import Icon from '@mui/material/Icon'
import { FlowServiceType } from '../../types/interface/fsm.interface'
import { ProductName } from '../../types/interface/quote.interface'

export const useStyles = makeStyles()((theme: Theme) => ({
  newQuoteButton: {
    display: 'flex',
    width: '100%',
    height: 230,
    padding: 40,
    borderRadius: 10,
    flexDirection: 'column',
    // No `gap`: on v4 the children sat in one `label` span, so it never applied between them.
    '&:hover': {
      backgroundColor: `${theme.brand.colors.main}10`,
    },
    [theme.breakpoints.down('md')]: {
      background: theme.brand.colors.main,
      padding: '5px 15px',
      height: 48,
      ...theme.brand.assets.layout?.mobileButton,
    },
    [theme.breakpoints.up('md')]: {
      '& .MuiTouchRipple-root': {
        color: theme.brand.colors.main,
      },
    },
    '& p': {
      marginTop: '0.35rem',
    },
  },

  icon: {
    color: theme.brand.colors.main,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  plusButtonContainer: {
    [theme.breakpoints.down('md')]: {
      position: 'fixed',
      // Anchor to the viewport: v5 Grid's -24px container margin otherwise shifts the static position.
      left: 0,
      bottom: 0,
      width: '100%',
      padding: '12px 16px !important',
    },
    ...theme.brand.assets.layout?.policiesListCardsPlusButtonContainer,
  },

  cardsWrapper: {
    position: 'relative',
    marginBottom: 56,
    // v4 Grid padded items on all sides; v5 drops the bottom half of the spacing.
    paddingBottom: theme.spacing(1.5),
  },

  policyListPlusButton: {
    textTransform: 'uppercase',
    ...theme.brand.assets.layout?.policyListPlusButton,
  },

  newQuoteButtonSecondary: {
    [theme.breakpoints.down('md')]: {
      background: 'transparent !important',
      color: `${theme.brand.colors.main} !important`,
      border: `1px solid ${theme.brand.colors.main}`,
      '& p': {
        color: theme.brand.colors.main,
      },
    },
  },
}))

interface PoliciesList {
  policies: Partial<PolicyV2Type>[]
  service: FlowServiceType
  plusButtonHandler?: () => void
  additionalOffers?: React.ReactNode
  onAvtaleGiroClick?: (policyNumber: string) => void
}

const PoliciesListCards: FC<PoliciesList> = ({
  service,
  plusButtonHandler,
  policies,
  additionalOffers,
  onAvtaleGiroClick,
}) => {
  const { t } = useTranslate(T.POLICIES)
  const { classes } = useStyles()
  const { setContext } = useContext(Context)
  const hasAvtalegiroAction =
    !!onAvtaleGiroClick && policies.some((p) => p.PaymentMethod !== PAYMENT_METHOD_AVTALEGIRO)

  const getExtraContentCount = (policy: Partial<PolicyV2Type>): number => {
    let count = 0
    if (policy.productName === ProductName.VEHICLE && policy.NeedsOdometerAdjustment) count++
    if (!!onAvtaleGiroClick && policy.PaymentMethod !== PAYMENT_METHOD_AVTALEGIRO) count++
    return count
  }

  const sortedPolicies = [...policies].sort(
    (a, b) => getExtraContentCount(b) - getExtraContentCount(a)
  )

  const handleClaimCreation = async (policyNumber: string) => {
    setContext({ [SELECTED_POLICY_NUMBER]: policyNumber })
    service.send({ type: Events.NEXT, policyId: policyNumber })
  }

  const handleMileageClick = (policyNumber: string) => {
    setContext({ [SELECTED_POLICY_NUMBER]: policyNumber })
    service.send({ type: Events.MILEAGE })
  }

  return (
    <Grid
      container
      spacing={3}
      justifyContent={'center'}
      data-testid={'policy-list'}
      className={classes.cardsWrapper}
    >
      {sortedPolicies.map((policy: Partial<PolicyV2Type>, key) => (
        <Grid key={`${policy.PolicyNumber}-${key}`} item xs={12} md={4}>
          <PolicyCard
            data={policy as PolicyV2Type}
            handleClick={() => handleClaimCreation(policy?.PolicyNumber?.toString() ?? '')}
            onMileageClick={() => handleMileageClick(policy?.PolicyNumber?.toString() ?? '')}
            onCreateAvtalegiro={
              onAvtaleGiroClick
                ? () => onAvtaleGiroClick(policy?.PolicyNumber?.toString() ?? '')
                : undefined
            }
          />
        </Grid>
      ))}
      {additionalOffers}
      {plusButtonHandler && (
        <Grid key='plus_button' item xs={12} md={4} className={classes.plusButtonContainer}>
          <Button
            onClick={plusButtonHandler}
            className={`${classes.newQuoteButton}${hasAvtalegiroAction ? ` ${classes.newQuoteButtonSecondary}` : ''}`}
          >
            <Icon className={classes.icon} fontSize='large'>
              add_circle
            </Icon>
            <Typography
              testId='new-quote-button'
              modifiers={['bold']}
              className={classes.policyListPlusButton}
            >
              {t('newQuoteButton')}
            </Typography>
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default PoliciesListCards
